/* eslint-disable */
// Legacy flag/registry awareness (gated by REACT_APP_SMARTNAV_PRELOAD, every environment).
//
// When preload is ON the legacy stops being a dumb redirector: it polls Unleash and fetches
// the shell's registry (runtime, cached), then bounces to the shell ONLY for routes the
// shell actually owns AND has activated — so legacy-owned routes navigate natively (no
// legacy->shell->legacy round-trip). When preload is OFF, behaviour is unchanged (the
// SmartLink keeps unconditionally bouncing to the shell).
//
// This used to be gated on `SERVER_ENV` (REACT_APP_ENV !== 'local') too, which left local dev
// as a dumb redirector after the Vite middleware was removed — so local no longer obeyed the
// flags/registry. Flag-awareness is now env-independent (mirroring the shell's mount-time guard,
// which already runs in every environment); local turns it on via REACT_APP_SMARTNAV_PRELOAD.

import { allowRedirect, clearLoopGuard, decide, type Verdict } from './decisionCore';
import { cookies } from 'sharedKernel/cookies/behavior';

const PRELOAD = (process.env.REACT_APP_SMARTNAV_PRELOAD ?? '') === 'true';

const SHELL_URL = process.env.REACT_APP_SHELL_URL ?? 'http://localhost:3000';
const TENANT_DOMAIN =
  process.env.REACT_APP_TENANT_DOMAIN ?? process.env.TENANT_DOMAIN ?? 'localhost';
// The feature-flag endpoint lives on the official backend (REACT_APP_BASE_API_URL), same host
// as every other API route — derived here, so no dedicated REACT_APP_UNLEASH_PROXY_URL is needed.
const BASE_API_URL = process.env.REACT_APP_BASE_API_URL ?? '';
const UNLEASH_URL = BASE_API_URL
  ? `${BASE_API_URL.replace(/\/+$/, '')}/v2/feature-flags/domain/${encodeURIComponent(TENANT_DOMAIN)}/flags`
  : '';
const REGISTRY_URL = process.env.REACT_APP_REGISTRY_URL ?? SHELL_URL + '/registry.json';
const ESCAPE = (process.env.REACT_APP_LEGACY_PATH_PREFIXES ?? '')
  .split(',').map((s) => s.trim()).filter(Boolean);
const POLL_MS = parseInt(process.env.UNLEASH_POLL_INTERVAL_MS ?? '15000', 10);
const FAIL_TOWARD_LEGACY_MS = 1500;

const TOGGLE_KEY = 'zing:smartnav:toggles:' + TENANT_DOMAIN;
const REGISTRY_KEY = 'zing:smartnav:registry:' + TENANT_DOMAIN;

let _toggles: Record<string, boolean> = {};
let _registry: string[] = [];
let _ready = false;
let _init: Promise<void> | null = null;

export function isLegacyPreloadEnabled(): boolean {
  return PRELOAD;
}

function hydrate(): void {
  try {
    const r = localStorage.getItem(TOGGLE_KEY);
    const p = r ? JSON.parse(r) : null;
    if (p && p.t) _toggles = p.t;
  } catch { /* ignore */ }
  try {
    const r = localStorage.getItem(REGISTRY_KEY);
    const p = r ? JSON.parse(r) : null;
    if (Array.isArray(p)) _registry = p;
  } catch { /* ignore */ }
}

async function refreshToggles(): Promise<void> {
  // The feature-flag endpoint requires a user JWT. Pre-login there is no token, so skip the
  // poll, keep the stale cache, and never bounce — the legacy renders in place. The token is
  // shared with the shell via the `_cred` cookie.
  const token = cookies.get('_cred')?.data?.token as string | undefined;
  if (!UNLEASH_URL || !token) return;
  try {
    const res = await fetch(UNLEASH_URL, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return;
    const json = await res.json();
    const next: Record<string, boolean> = {};
    for (const t of (json.toggles ?? [])) next[t.name] = t.enabled === true;
    _toggles = next;
    try { localStorage.setItem(TOGGLE_KEY, JSON.stringify({ t: next, ts: Date.now() })); } catch { /* ignore */ }
  } catch { /* keep stale */ }
}

async function refreshRegistry(): Promise<void> {
  try {
    const res = await fetch(REGISTRY_URL);
    if (!res.ok) return;
    const json = await res.json();
    if (Array.isArray(json)) {
      _registry = json;
      try { localStorage.setItem(REGISTRY_KEY, JSON.stringify(json)); } catch { /* ignore */ }
    }
  } catch { /* keep stale; on total failure decide() can only say 'legacy' (safe) */ }
}

export function initLegacySmartNav(): Promise<void> {
  if (_init) return _init;
  hydrate();
  // Instant path only when BOTH caches are warm: decide() needs the registry to answer "does the
  // shell own this path?" and the toggles to answer "is it active?". A half-warm cache passed as
  // ready and produced a wrong verdict; falling through to _init (bounded by
  // FAIL_TOWARD_LEGACY_MS) is correct and still fast.
  if (Object.keys(_toggles).length > 0 && _registry.length > 0) _ready = true; // stale-while-revalidate
  _init = Promise.all([refreshToggles(), refreshRegistry()]).then(() => { _ready = true; });
  setInterval(() => { refreshToggles(); refreshRegistry(); }, POLL_MS);
  return _init;
}

export function decideLegacy(pathname: string): Verdict {
  const token = cookies.get('_cred')?.data?.token as string | undefined;
  return decide(pathname, {
    registry: _registry,
    toggles: _toggles,
    tenantDomain: TENANT_DOMAIN,
    escapeHatchPrefixes: ESCAPE,
    isAuthenticated: token != null,
  });
}

/** For the on-load gate: resolve immediately if ready, else wait for the first refresh
 *  with a fail-toward-legacy timeout (so a slow/down Unleash never hangs). */
export function ensureLegacyDecision(pathname: string): Promise<Verdict> {
  if (_ready) return Promise.resolve(decideLegacy(pathname));
  return Promise.race([
    _init ?? Promise.resolve(),
    new Promise<void>((resolve) => setTimeout(resolve, FAIL_TOWARD_LEGACY_MS)),
  ]).then(() => decideLegacy(pathname));
}

export function toShellUrl(path: string, withQuery = false): string {
  const n = path.startsWith('/') ? path : '/' + path;
  return withQuery ? SHELL_URL + n + window.location.search + window.location.hash : SHELL_URL + n;
}

/** Loop-guarded redirect to the shell. Returns false if the loop guard tripped. */
export function redirectToShell(path: string, withQuery = false): boolean {
  if (!allowRedirect(path)) return false;
  window.location.replace(toShellUrl(path, withQuery));
  return true;
}

export function markSettled(): void {
  clearLoopGuard();
}

export { SHELL_URL };
